import { useMemo } from "react";

import {
    BsMetaUtil,
    TokenBalanceExpanded,
    useAbfFetches,
    useLoopscaleWalletContext,
    useTokenListContext,
    useWalletBalanceByMint,
    LendVaultSortType,
    lendingVaultsPublicApi,
    useUserTokens
} from "@bridgesplit/abf-react";
import {
    FONT_SIZES,
    Icon,
    RefreshButtonWithLoading,
    Row,
    Text,
    TextVariant,
    Tooltip,
    useAppPalette
} from "@bridgesplit/ui";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { COPY, TEST_IDS } from "app/constants";
import { roundDownToDecimals, SortDirection } from "@bridgesplit/utils";
import { TokenListMetadata } from "@bridgesplit/abf-sdk";

type BalanceProps = {
    variant?: TextVariant;
    maxText?: string;
    hideRefresh?: boolean;
    helpText?: string;
    hideInsufficientBalance?: boolean;
    isLoading: boolean;
    hideRowEnd?: boolean;
    setMax?: (amount: number) => void;
    balance: number;
    escrowNeeded?: boolean;
    metadata: TokenListMetadata | undefined;
    skipProps?: boolean; // will skip balance check, used if input does not require input show
    vaultAddress?: string; // needed for RefreshBalance if on vault page
    refresh?: () => void;
    isRefreshing?: boolean;
    insufficientBalance: boolean;
    maxButtonCta?: string;
    disabled?: boolean;
};

export function useBalanceChecker({
    amount,
    mint,
    skip: skipProps,
    customBalance,
    solanaFeeBuffer
}: BalanceCheckOptions) {
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();
    const { getMetadata } = useTokenListContext();

    const skip = skipProps || !activeWallet;
    const walletBalance = useWalletBalanceByMint(activeWallet, mint, {
        skip,
        fullSolBalance: false,
        solBuffer: solanaFeeBuffer
    });

    const { isLoading: isLoadingRaw, balance } = useMemo(() => {
        if (customBalance !== undefined) return { balance: customBalance, isLoading: false };

        return { balance: walletBalance.data?.amount ?? 0, isLoading: walletBalance.isLoading };
    }, [customBalance, walletBalance]);

    const isLoading = isLoadingRaw && !skip;

    const metadata = getMetadata(mint);

    const insufficientBalance = !skipProps && amount && balance !== undefined ? balance < amount : false;

    const tokenBalance: TokenBalanceExpanded | undefined = metadata
        ? { key: metadata.mint, metadata, amount: balance ?? 0 }
        : undefined;

    return {
        balance,
        tokenBalance,
        insufficientBalance,
        isLoading
    };
}

type BalanceCheckOptions = {
    amount: number | undefined;
    mint: string | undefined;
    skip?: boolean;
    customBalance?: number;
    solanaFeeBuffer?: number;
};

function RefreshWalletButton({ variant }: { variant: TextVariant }) {
    const { refresh, isLoading } = useUserTokens();

    return (
        <RefreshButtonWithLoading
            variant={variant}
            isFetching={isLoading}
            callback={() => {
                void refresh();
            }}
        />
    );
}

function RefreshVaultWithdrawButton({
    vaultAddress,
    variant
}: {
    vaultAddress: string | undefined;
    variant: TextVariant;
}) {
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();
    const { resetLendingVaultPublicApi, resetLendingVaultPositionsApi } = useAbfFetches();

    const { isFetching } = lendingVaultsPublicApi.endpoints.lendingVaultInfos.useQueryState(
        activeWallet && vaultAddress
            ? {
                  vaultAddresses: [vaultAddress],
                  principalMints: [vaultAddress],
                  lpMints: [vaultAddress],
                  depositsEnabled: true,
                  sortType: LendVaultSortType.TotalDeposits,
                  sortDirection: SortDirection.Desc,
                  page: 0,
                  pageSize: 5
              }
            : skipToken
    );

    const callback = () => {
        resetLendingVaultPublicApi();
        resetLendingVaultPositionsApi();
    };

    return <RefreshButtonWithLoading variant={variant} isFetching={isFetching} callback={callback} />;
}

export function BalanceDisplay(props: BalanceProps) {
    const { isLoading, hideRowEnd, variant, setMax, balance, maxButtonCta, disabled, metadata } = props;
    if (isLoading) return <Row sx={{ height: FONT_SIZES.body1 + "px" }} />;
    if (hideRowEnd) return <Balance {...props} />;

    const onchainBalance = roundDownToDecimals(balance, metadata?.decimals);
    return (
        <Row spaceBetween>
            <Balance {...props} />

            <RowEnd
                setMax={setMax}
                balance={onchainBalance}
                hideRowEnd={hideRowEnd}
                variant={variant}
                maxButtonCta={maxButtonCta}
                disabled={disabled}
            />
        </Row>
    );
}

function RowEnd({
    variant = "body2",
    hideRowEnd,
    setMax,
    balance,
    maxButtonCta = "Max",
    disabled
}: {
    variant?: TextVariant;
    hideRowEnd?: boolean;
    setMax?: (amount: number) => void;
    balance: number | undefined;
    maxButtonCta?: string;
    disabled?: boolean;
}) {
    if (hideRowEnd) return null;
    if (setMax && !!balance) {
        return (
            <Text
                dataTestId={TEST_IDS.BALANCE_DISPLAY_MAX_BUTTON}
                color="secondary"
                variant={variant}
                onClick={disabled ? undefined : () => setMax(balance)}
            >
                {maxButtonCta}
            </Text>
        );
    }
    return null;
}

function RefreshBalance({
    variant,
    vaultAddress,
    refresh,
    isRefreshing
}: {
    variant: TextVariant;
    vaultAddress?: string;
    refresh?: () => void;
    isRefreshing?: boolean;
}) {
    if (refresh) {
        return <RefreshButtonWithLoading variant={variant} isFetching={!!isRefreshing} callback={refresh} />;
    }
    if (vaultAddress) {
        return <RefreshVaultWithdrawButton variant={variant} vaultAddress={vaultAddress} />;
    }
    return <RefreshWalletButton variant={variant} />;
}

function Balance({
    variant = "body2",
    maxText,
    hideRefresh,
    helpText,
    hideInsufficientBalance,
    escrowNeeded,
    metadata,
    balance,
    insufficientBalance,
    skipProps,
    vaultAddress,
    refresh,
    isRefreshing
}: BalanceProps) {
    const { error } = useAppPalette();
    const defaultMaxText = escrowNeeded ? COPY.ESCROW_TERM : "Wallet";
    return (
        <Row spacing={0.5}>
            <Text color="caption" variant={variant}>
                {maxText ?? defaultMaxText}:
            </Text>
            <Text color="caption" variant={variant}>
                {BsMetaUtil.formatAmount(metadata, skipProps ? 0 : balance ?? 0)}
            </Text>
            {helpText && (
                <Tooltip title={helpText}>
                    <Text color="disabled" variant={variant}>
                        <Icon type="tooltip" />
                    </Text>
                </Tooltip>
            )}
            {!hideRefresh && (
                <RefreshBalance
                    variant={variant}
                    vaultAddress={vaultAddress}
                    refresh={refresh}
                    isRefreshing={isRefreshing}
                />
            )}

            {insufficientBalance && !hideInsufficientBalance && (
                <Tooltip title="Insufficient balance">
                    <Text variant={variant}>
                        <Icon sx={{ color: error }} type="warning" />
                    </Text>
                </Tooltip>
            )}
        </Row>
    );
}
